import { useModal } from 'modals';
import { useEffect } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { useSnapshot } from 'valtio';

import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';
import { StripeProduct } from '@cartwhl/common/types';
import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import { SegmentedControl } from '@cartwhl/ui/atoms/Switch';
import Text from '@cartwhl/ui/base/TextNew';
import LockIcon from '@cartwhl/ui/icons/LockIcon';

import GreenLink from 'components/GreenLink';

import { getStripeCustomerIdForBilling } from '@cartwhl/common/utils/organizationWorkspace';

import { customerStore, userStore } from '../../../stores/global';
import PlatformCard from './PlatformCard';
import PricingCreditsExplainerSection, {
  PRICING_CREDITS_EXPLAINER_ID,
} from './PricingCreditsExplainerSection';
import UpdatedPlanCard from './UpdatedPlanCard';
import {
  type BillingMode,
  type PlatformConfig,
  type PlatformDetailSection,
  type PricingFooterCtaConfig,
  type PricingMatrixColumn,
  type PricingMatrixConfig,
  type PricingMatrixSection,
  type PricingMatrixValueItem,
  type PricingMatrixValueTone,
  type PricingTabSlug,
  pricingTabConfigs,
  pricingTabs,
} from './pricingPageData';

const DEFAULT_PRICING_TAB: PricingTabSlug = 'individual-plans';
const DEFAULT_BILLING_MODE: BillingMode = 'yearly';

export default function PricingPage() {
  const { tab } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { open } = useModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userInfo, organizationInfo } = useSnapshot(userStore.state);
  const { products } = useSnapshot(updatedProductStore.state);

  const activeTab = isPricingTabSlug(tab) ? tab : DEFAULT_PRICING_TAB;
  const rawBillingMode = searchParams.get('billing');
  const billingMode = isBillingMode(rawBillingMode)
    ? rawBillingMode
    : DEFAULT_BILLING_MODE;

  useEffect(() => {
    updatedProductStore.fetchProducts();
  }, []);

  const stripeCustomerIdForBilling = getStripeCustomerIdForBilling(
    userInfo,
    organizationInfo,
  );

  useEffect(() => {
    if (stripeCustomerIdForBilling) {
      customerStore.fetchCustomer(stripeCustomerIdForBilling);
    }
  }, [stripeCustomerIdForBilling]);

  const savings = updatedProductStore.getSavings('individual_pro');
  const interval = billingMode === 'yearly' ? 'year' : 'month';
  const activeConfig = pricingTabConfigs[activeTab];
  const featuredProduct = activeConfig.featuredProductKey
    ? products[activeConfig.featuredProductKey]
    : null;
  const featuredPrice = featuredProduct
    ? updatedProductStore.getPriceByProductAndMode(featuredProduct.id, interval)
    : null;

  if (tab !== activeTab || rawBillingMode !== billingMode) {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('billing', billingMode);

    return (
      <Navigate
        replace
        to={`${getPricingLocation(activeTab, nextSearchParams)}${location.hash}`}
      />
    );
  }

  async function handleFooterTrialClick() {
    if (!featuredProduct?.id || !featuredPrice) {
      return;
    }
    // scroll them up to the pricing cards
    window.scrollTo({
      top: document.getElementById('pricing-title')?.offsetTop ?? 0,
      behavior: 'smooth',
    });
  }

  function handleBillingModeChange(nextBillingMode: BillingMode) {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('billing', nextBillingMode);
    setSearchParams(nextSearchParams, { preventScrollReset: true });
  }

  function handleCreditsExplainerClick() {
    document
      .getElementById(PRICING_CREDITS_EXPLAINER_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <main className="mb-32">
      <section className="container flex flex-col gap-8 pb-10 pt-10 lg:gap-16 lg:pb-12 lg:pt-[80px]">
        <div className="flex flex-col gap-7 lg:gap-20">
          <Text
            id="pricing-title"
            as="h1"
            token={{ base: 'display/36/bold', lg: 'display/64/bold' }}
            className="text-pretty"
          >
            Pricing plans
          </Text>
          <div className="flex flex-col gap-5 lg:gap-10">
            <Text
              as="p"
              token={{ base: '20/medium', lg: '36/medium' }}
              className="text-pretty"
            >
              Choose the plan that fits how you work—from individual creation to
              team collaboration to production‑scale API workflows.
            </Text>
            {activeConfig.showCreditsExplainer && (
              <Text
                as="p"
                token={{ base: '16', lg: '24/medium' }}
                className="text-tertiary"
              >
                <span className="text-primary">
                  Plans include access to Cartwheel features and a monthly
                  credit allotment.
                </span>{' '}
                Credits are used to capture and generate motion. Different
                workflows consume credits based on duration and complexity. As
                usage grows, you can purchase more capacity and unlock higher
                throughput, collaboration, and support.{' '}
                <button
                  type="button"
                  onClick={handleCreditsExplainerClick}
                  className="text-blue font-inherit inline cursor-pointer appearance-none bg-transparent p-0 underline"
                >
                  Learn how credits work.
                </button>
              </Text>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-8 lg:gap-8">
          <PricingTabs
            activeTab={activeTab}
            searchParams={searchParams}
            maxWidthClassName={activeConfig.tabsMaxWidthClassName}
          />
          {activeConfig.showBillingToggle && (
            <BillingToggle
              billingMode={billingMode}
              savings={savings}
              onChange={handleBillingModeChange}
            />
          )}
        </div>
      </section>

      <section className="container">
        {activeConfig.cards && (
          <div
            id="pricing-cards"
            className={`grid gap-4 ${
              activeConfig.cards.length === 2
                ? 'lg:grid-cols-2'
                : 'lg:grid-cols-3'
            }`}
          >
            {activeConfig.cards.map((card) => (
              <UpdatedPlanCard
                key={card.productKey}
                product={products[card.productKey] as StripeProduct | null}
                displayName={card.title}
                description={card.description}
                creditsValue={card.creditsValue}
                creditsPeriodLabel={card.creditsPeriodLabel}
                interval={interval}
                periodLabel={card.periodLabel[billingMode]}
                accent={card.accent}
                showInlineYearlyTotal={true}
                yearlyDisplayPrice={card.yearlyDisplayPrice}
                yearlyTotalLabel={card.yearlyTotalLabel}
                onCreditsClick={handleCreditsExplainerClick}
              />
            ))}
          </div>
        )}

        {activeConfig.platform && (
          <PlatformCard
            titlePrefix={activeConfig.platform.titlePrefix}
            titleSuffix={activeConfig.platform.titleSuffix}
            features={activeConfig.platform.features}
            onContactClick={() =>
              open('ENTERPRISE_CONTACT', { reason: 'pricing_plans' })
            }
          />
        )}
      </section>

      {activeConfig.matrix && <ComparisonMatrix config={activeConfig.matrix} />}

      {activeConfig.showCreditsExplainer && <PricingCreditsExplainerSection />}

      {activeConfig.platform && (
        <PlatformDetails config={activeConfig.platform} />
      )}

      <section className="container mt-20 flex flex-col gap-14 lg:mt-24 lg:gap-16">
        <FooterCta
          config={activeConfig.footerCta}
          onClick={() => {
            if (activeConfig.platform) {
              open('ENTERPRISE_CONTACT', { reason: 'pricing_plans' });
              return;
            }

            handleFooterTrialClick();
          }}
        />
      </section>
    </main>
  );
}

interface PricingTabsProps {
  activeTab: PricingTabSlug;
  searchParams: URLSearchParams;
  maxWidthClassName: string;
}

function PricingTabs({
  activeTab,
  searchParams,
  maxWidthClassName,
}: PricingTabsProps) {
  const navigate = useNavigate();

  return (
    <SegmentedControl
      ariaLabel="Pricing plan category"
      options={pricingTabs.map((tab) => ({
        label: tab.label,
        mobileLabel: tab.shortLabel,
        value: tab.slug,
      }))}
      value={activeTab}
      onValueChange={(nextTab) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set(
          'billing',
          searchParams.get('billing') ?? DEFAULT_BILLING_MODE,
        );

        navigate(
          getPricingLocation(nextTab as PricingTabSlug, nextSearchParams),
          {
            preventScrollReset: true,
            state: { preventScrollReset: true },
          },
        );
      }}
      token={{ base: '16/medium', lg: '20/medium' }}
      buttonClassName="h-10"
      stretch={true}
      className={`w-full ${maxWidthClassName}`}
    />
  );
}

interface BillingToggleProps {
  billingMode: BillingMode;
  savings: number | null;
  onChange: (nextBillingMode: BillingMode) => void;
}

function BillingToggle({ billingMode, savings, onChange }: BillingToggleProps) {
  const yearlyLabel = savings ? `Pay Yearly - ${savings}% off` : 'Pay Yearly';

  return (
    <div
      role="radiogroup"
      className="flex flex-wrap items-center justify-center gap-6 lg:gap-8"
    >
      <Checkbox
        id="pricing-billing-monthly"
        checked={billingMode === 'monthly'}
        align="center"
        label="Pay Monthly"
        labelToken={{ base: '14', lg: '22/medium' }}
        textClassName={
          billingMode === 'monthly' ? 'text-primary' : 'text-secondary'
        }
        setChecked={(checked) => {
          if (checked) {
            onChange('monthly');
          }
        }}
      />
      <Checkbox
        id="pricing-billing-yearly"
        checked={billingMode === 'yearly'}
        align="center"
        label={yearlyLabel}
        labelToken={{ base: '14', lg: '22/medium' }}
        textClassName={
          billingMode === 'yearly' ? 'text-primary' : 'text-secondary'
        }
        setChecked={(checked) => {
          if (checked) {
            onChange('yearly');
          }
        }}
      />
    </div>
  );
}

interface ComparisonMatrixProps {
  config: PricingMatrixConfig;
}

function ComparisonMatrix({ config }: ComparisonMatrixProps) {
  return (
    <section className="container mt-16 flex flex-col gap-8 lg:mt-20 lg:gap-10">
      <div
        className={`hidden gap-4 lg:grid ${
          config.columns.length === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-3'
        }`}
      >
        {config.columns.map((column) => (
          <MatrixColumnHeader
            key={column.label}
            column={column}
            compact={false}
          />
        ))}
      </div>

      <div className="flex flex-col gap-14 lg:hidden">
        <Text as="h2" token="display/36/bold" className="text-pretty">
          {config.title}
        </Text>
        <div
          className={`grid gap-4 ${
            config.columns.length === 2 ? 'grid-cols-2' : 'grid-cols-3'
          }`}
        >
          {config.columns.map((column) => (
            <MatrixColumnHeader
              key={column.label}
              column={column}
              compact={true}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-8 lg:gap-10">
        {config.sections.map((section, index) => (
          <ComparisonMatrixSectionBlock
            key={section.label}
            section={section}
            columnCount={config.columns.length}
            followsHighlighted={Boolean(
              config.sections[index - 1]?.highlighted,
            )}
          />
        ))}
      </div>
    </section>
  );
}

interface MatrixColumnHeaderProps {
  column: PricingMatrixColumn;
  compact: boolean;
}

function MatrixColumnHeader({ column, compact }: MatrixColumnHeaderProps) {
  return (
    <div className="flex items-center gap-2 lg:gap-3">
      {column.accent !== 'neutral' && (
        <span
          className={`rounded-full ${
            compact ? 'h-3 w-3' : 'h-6 w-6'
          } ${column.accent === 'success' ? 'bg-green-50' : 'bg-blue'}`}
        />
      )}
      <Text as="div" token={compact ? '16/bold' : 'display/34/bold'}>
        {column.label}
      </Text>
    </div>
  );
}

interface ComparisonMatrixSectionBlockProps {
  section: PricingMatrixSection;
  columnCount: number;
  followsHighlighted: boolean;
}

function ComparisonMatrixSectionBlock({
  section,
  columnCount,
  followsHighlighted,
}: ComparisonMatrixSectionBlockProps) {
  return (
    <div
      className={
        section.highlighted
          ? 'xs:-mx-8 xs:px-8 -mx-4 rounded-2xl border border-yellow-50 bg-yellow-50/10 px-4 py-5 lg:py-8'
          : followsHighlighted
            ? ''
            : 'border-primary border-t pt-4 lg:pt-8'
      }
    >
      <Text
        as="h3"
        token={{ base: '16/medium', lg: 'display/24/bold' }}
        className="text-secondary lg:text-primary text-prett mb-4 flex h-10 items-center lg:mb-8"
      >
        {section.label}
      </Text>
      <div
        className={`grid gap-4 lg:gap-6 ${
          columnCount === 2 ? 'grid-cols-2' : 'grid-cols-3'
        }`}
      >
        {section.values.map((values, index) => (
          <ComparisonMatrixCell
            key={`${section.label}-${index}`}
            values={values}
          />
        ))}
      </div>
    </div>
  );
}

interface ComparisonMatrixCellProps {
  values: PricingMatrixValueItem[];
}

function ComparisonMatrixCell({ values }: ComparisonMatrixCellProps) {
  return (
    <div className="flex flex-col gap-6 lg:gap-8">
      {values.map((value) => {
        const item: {
          label: string;
          tone: PricingMatrixValueTone;
          icon?: 'lock';
        } =
          typeof value === 'string'
            ? {
                label: value,
                tone: value === '-' ? 'secondary' : 'primary',
              }
            : { tone: 'primary', ...value };

        const textClassName =
          item.tone === 'secondary'
            ? 'text-secondary'
            : item.tone === 'tertiary'
              ? 'text-tertiary'
              : '';

        if (item.icon === 'lock') {
          return (
            <div key={item.label} className="flex items-center gap-3">
              <LockIcon
                className={`h-5 w-5 shrink-0 ${textClassName} lg:h-6 lg:w-6`}
              />
              <Text
                as="div"
                token={{ base: '16/medium', lg: '24/medium' }}
                className={textClassName}
              >
                {item.label}
              </Text>
            </div>
          );
        }

        return (
          <Text
            key={item.label}
            as="div"
            token={{ base: '16/medium', lg: '24/medium' }}
            className={textClassName}
          >
            {item.label}
          </Text>
        );
      })}
    </div>
  );
}

interface PlatformDetailsProps {
  config: PlatformConfig;
}

function PlatformDetails({ config }: PlatformDetailsProps) {
  return (
    <section className="container mt-16 flex flex-col gap-4 lg:mt-20 lg:gap-10">
      <div className="flex flex-col gap-10">
        <Text
          as="h2"
          token={{ base: 'display/36/bold', lg: 'display/34/bold' }}
          className="text-pretty"
        >
          {config.detailTitle}
        </Text>
        <Text as="p" token="24/medium" className="text-pretty">
          {config.detailLead}
          <span className="text-primary decoration-purple decoration-3 underline underline-offset-2">
            {config.detailLinkLabel}
          </span>
          {config.detailTail}
        </Text>
        {config.detailListLead && (
          <Text as="p" token="24/medium" className="text-pretty lg:hidden">
            {config.detailListLead}
          </Text>
        )}
      </div>

      <div className="hidden lg:flex lg:flex-col">
        {config.sections.map((section) => (
          <DesktopPlatformDetailRow key={section.label} section={section} />
        ))}
      </div>

      <div className="flex flex-col lg:hidden">
        {config.sections.map((section, index) => (
          <MobilePlatformDetailRow
            key={section.label}
            section={section}
            isFirst={index === 0}
          />
        ))}
      </div>
    </section>
  );
}

interface DesktopPlatformDetailRowProps {
  section: PlatformDetailSection;
}

function DesktopPlatformDetailRow({ section }: DesktopPlatformDetailRowProps) {
  return (
    <div className="border-primary grid grid-cols-[220px_minmax(0,1fr)] gap-6 border-t py-8 lg:grid-cols-[320px_minmax(0,1fr)]">
      <Text as="div" token="24/medium" className="text-secondary">
        {section.label}
      </Text>
      <div className="flex flex-col gap-6 lg:gap-8">
        {section.values.map((value) => (
          <Text key={value} as="div" token="24/medium">
            {value}
          </Text>
        ))}
      </div>
    </div>
  );
}

interface MobilePlatformDetailRowProps {
  section: PlatformDetailSection;
  isFirst: boolean;
}

function MobilePlatformDetailRow({
  section,
  isFirst,
}: MobilePlatformDetailRowProps) {
  const className = section.highlighted
    ? '-mx-4 my-4 rounded-2xl border border-yellow-50 bg-yellow-50/10 px-4 py-5'
    : `${isFirst ? '' : 'border-primary border-t '}pt-5 pb-8`;

  return (
    <div className={className}>
      <Text as="div" token="16" className="text-tertiary mb-6">
        {section.mobileLabel ?? section.label}
      </Text>
      <div className="flex flex-col gap-6">
        {section.values.map((value) => (
          <Text key={value} as="div" token="16">
            {value}
          </Text>
        ))}
      </div>
    </div>
  );
}

interface FooterCtaProps {
  config: PricingFooterCtaConfig;
  onClick: () => void;
}

function FooterCta({ config, onClick }: FooterCtaProps) {
  const linkClassName =
    config.tone === 'purple'
      ? 'text-purple-60 hover:text-purple-50'
      : undefined;

  return (
    <div className="flex flex-col gap-8 lg:gap-20">
      <div className="flex flex-col gap-8 lg:gap-10">
        <Text
          as="h2"
          token={{ base: 'display/36/bold', lg: 'display/64/bold' }}
          className="text-pretty"
        >
          {config.title}
        </Text>
        <Text
          as="p"
          token={{ base: '20/medium', lg: '24/medium' }}
          className="max-w-[848px] text-pretty"
        >
          {config.body}
        </Text>
      </div>

      <GreenLink
        onClick={onClick}
        className={linkClassName}
        token={{ base: 'display/20/bold', lg: 'display/34/bold' }}
      >
        {config.label}
      </GreenLink>
    </div>
  );
}

function isPricingTabSlug(value: string | undefined): value is PricingTabSlug {
  return (
    value === 'team-plans' ||
    value === 'individual-plans' ||
    value === 'platform-plans'
  );
}

function isBillingMode(value: string | null): value is BillingMode {
  return value === 'monthly' || value === 'yearly';
}

function getPricingLocation(
  tab: PricingTabSlug,
  searchParams: URLSearchParams,
) {
  const search = searchParams.toString();
  return search ? `/pricing/${tab}?${search}` : `/pricing/${tab}`;
}
